import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subscription } from 'rxjs';

import { AuthService } from './auth.service';
import { Archetype } from '../domainTypes/ddr';
import { environment } from '../../environments/environment';
import { ThreadEventsService } from './thread-events.service';
import { ToastService } from './toast.service';

const ROOT = `${environment.apiBase}/api/ddr`;
const POLL_INTERVAL_MS = 60_000;

/** Scoped by user id: a shared browser must not hand one account's Activity
 *  scope to the next account that signs in. */
const teamWideKey = (userId: number) => `ciq.activity.teamWide.${userId}`;
function readTeamWide(userId: number | null): boolean {
    // Team is the default (Evan, 2026-09-16): one feed, every DDR's work.
    // Signed out reads the default as well.
    if (userId == null) {
        return true;
    }
    try {
        const stored = localStorage.getItem(teamWideKey(userId));
        return stored === null ? true : stored === '1';
    } catch {
        return true;
    }
}

export type ProactiveItemAction = {
    label: string;
    kind: 'approve' | 'dismiss' | 'never' | 'open_web_link' | 'send_drafted_reply' | 'ask_ddr' | string;
    method?: 'POST' | 'GET' | 'PUT' | 'DELETE';
    /** Absent on kinds the rail handles itself (review_email_draft, ask_ddr). */
    url?: string;
    /** ask_ddr: the message posted to the DDR's DM on the user's behalf. */
    prompt?: string;
    body?: Record<string, unknown>;
    style?: 'primary' | 'default' | 'danger';
    primary?: boolean;
};

export type ProactiveAttachment = {
    id?: string;
    name: string;
    content_type?: string;
    size?: number;
};

export type ProactiveItemView = {
    text: string;
    meta?: string;
    draft_reply?: string;
    body_text?: string;
    thread_text?: string;
    attachments?: ProactiveAttachment[];
    subject?: string;
    from?: string;
    /** Cc addresses on an outbound email approval - shown in the review
     *  dialog so the user sees EVERYONE the send reaches, not just To. */
    cc?: string[];
    received?: string;
    web_link?: string;
    message_id?: string;
    safety_note?: string;
    // Resolved outbound target (approval mode): the real recipient label + its
    // channel kind, so the review dialog shows who the send goes to and hides the
    // Subject row for non-email (Teams/calendar) sends.
    target_type?: string;
    target_label?: string | null;
    // Layer-2 high-signal fields (#246): who is asking what + the DDR's recommended move.
    why_you?: string;
    recommendation?: string;
    sent?: boolean;
    actions?: ProactiveItemAction[];
    // Teams chat / calendar context for the redesigned proactive surfaces.
    members?: string[];
    attendees?: string[];
    topic?: string;
    preview?: string;
};

export type ProactiveSection = {
    label: string;
    kind?: string;
    count: number;
    items: ProactiveItemView[];
};

export type ProactiveActivityItem = {
    id: string;
    kind: string;
    source: string | null;
    room_id?: string | null;
    persona_id: string | null;
    persona_display_name: string | null;
    persona_archetype: Archetype | null;
    title: string;
    teaser: string | null;
    /** One-line summary for the compact tile (job briefings). */
    summary?: string | null;
    /** Full markdown body for the detail view (job briefings). Rendered
     *  alongside any structured `sections`. */
    body?: string | null;
    sections?: ProactiveSection[];
    payload: Record<string, unknown>;
    created_at: string | null;
};

/** A scheduled job the DDR will run next (custom or default starter job),
 *  for the "Up next" surface. Sourced from ddr_jobs.next_run_at. */
export type UpcomingJob = {
    name: string;
    when: string | null;
    persona_id: string | null;
    persona_display_name: string | null;
};

/**
 * Right-rail proactive activity feed. Polls `/api/ddr/proactive/activity`
 * every 60s while authenticated; pauses when tab is hidden so we don't
 * thrash the API on idle browsers.
 */
/** Actions whose primary effect already happened when the backend answers
 *  `resolved: false`: Handle it sent the prompt to the DM, Send draft sent the
 *  mail. Those lock so they cannot fire twice; Dismiss did nothing durable and
 *  stays clickable for a retry. */
export function actionCannotRepeat(kind: string | undefined): boolean {
    return kind === 'ask_ddr' || kind === 'send_drafted_reply';
}

/** Body of an email resolution route (Send draft, Dismiss, Handle it):
 *  `resolved` false means the durable resolution the Activity feed filters
 *  on did not land, so the row may come back after a refresh. Send draft can
 *  also answer with the trust gate's verdict instead: `refused` (nothing was
 *  sent) or `queued` (an approval was created). */
export type ActionResult = {
    resolved?: boolean;
    warning?: string | null;
    refused?: boolean;
    queued?: boolean;
    reason?: string | null;
    error?: string | null;
    /** Human text beside a coded `error` (for example `no_email_connection`). */
    message?: string | null;
};

/** The sentence to show for a failed action: the human message when the
 *  route sent one, else the error text itself. */
export function actionErrorText(result: ActionResult | null | undefined): string {
    return (result?.message && String(result.message)) || (result?.error && String(result.error)) || 'request failed';
}

/** The message an email action is about, when its body names one. */
export function actionMessageId(action: { body?: Record<string, unknown> }): string | null {
    return action.body && typeof action.body['message_id'] === 'string' ? (action.body['message_id'] as string) : null;
}

export type ActionOutcome = 'done' | 'unresolved' | 'refused' | 'queued' | 'error';

/** What the backend actually did, read from the body. A 200 with no body
 *  keys is a plain success. A 200 carrying `error` (the tool's own failure:
 *  mail not connected, provider down) is a failure and nothing happened. */
export function actionOutcome(result: ActionResult | null | undefined): ActionOutcome {
    if (typeof result?.error === 'string' && result.error) {
        return 'error';
    }
    if (result?.refused === true) {
        return 'refused';
    }
    if (result?.queued === true) {
        return 'queued';
    }
    if (result?.resolved === false) {
        return 'unresolved';
    }
    return 'done';
}

@Injectable({ providedIn: 'root' })
export class ProactiveActivityService {
    private readonly auth = inject(AuthService);
    private readonly http = inject(HttpClient);
    private readonly threadEvents = inject(ThreadEventsService);
    private readonly toast = inject(ToastService);

    readonly items = signal<ProactiveActivityItem[]>([]);
    /** Upcoming scheduled jobs (soonest-first) - drives the "Up next" card. */
    readonly upcoming = signal<UpcomingJob[]>([]);
    readonly loading = signal<boolean>(false);
    readonly error = signal<string | null>(null);
    readonly count = computed(() => this.items().length);
    /** Currently-scoped room ids - set by whoever owns the rail surface
     *  (the open ddr-room-panel). Empty = global feed. */
    readonly scopeRoomIds = signal<string[]>([]);
    /** Team scope for the Activity feed (2026-09-15): when on, the feed
     *  ignores the open room and shows every DDR's work. One feed, several
     *  DDRs. Remembered per browser, per signed-in user. */
    readonly teamWide = signal<boolean>(readTeamWide(this.auth.state().user?.user_id ?? null));
    setTeamWide(value: boolean): void {
        this.teamWide.set(value);
        const userId = this.auth.state().user?.user_id;
        if (userId == null) {
            return;
        }
        try {
            localStorage.setItem(teamWideKey(userId), value ? '1' : '0');
        } catch {
            // Storage may be unavailable (private mode); the signal still applies for the session.
        }
    }
    /** Per-action lifecycle: '<itemId>::<sectionLabel>::<rowIndex>::<actionKey>'.
     *  firingKeys = currently in-flight; resolvedKeys = completed (locked
     *  out so a double-click can't re-fire). */
    readonly firingKeys = signal<ReadonlySet<string>>(new Set());
    readonly resolvedKeys = signal<ReadonlySet<string>>(new Set());
    /** Set of email message_ids the user has actioned this session
     *  (sent, dismissed, or corrected). Both the rail and the in-chat
     *  system-event card read this to suppress action buttons on rows
     *  that have already been handled, so the user doesn't see "Send
     *  draft" still showing after they hit Send. The audit-event-based
     *  filter on rail render covers the same ground on the next 60s
     *  poll; this signal handles the immediate / current-session UX. */
    readonly resolvedMessageIds = signal<ReadonlySet<string>>(new Set());

    markMessageResolved(messageId: string): void {
        if (!messageId) {
            return;
        }
        this.resolvedMessageIds.update(set => {
            if (set.has(messageId)) {
                return set;
            }
            const next = new Set(set);
            next.add(messageId);
            return next;
        });
    }

    isMessageResolved(messageId: string | undefined | null): boolean {
        return !!messageId && this.resolvedMessageIds().has(messageId);
    }

    /** Per-action key. Prefer a stable identifier (action.body.message_id
     *  or the action url+body shape) over the visual row index - when a
     *  row is spliced out optimistically, every row below shifts up and
     *  the rowIndex-keyed state would point at the wrong neighbor (the
     *  new row inherits the prior row's resolved state). */
    actionKey(itemId: string, sectionLabel: string, rowIndex: number, action: ProactiveItemAction): string {
        const stable = this.stableActionId(action);
        if (stable) {
            return `${itemId}::${sectionLabel}::${stable}::${action.kind ?? ''}::${action.label ?? ''}`;
        }
        return `${itemId}::${sectionLabel}::row${rowIndex}::${action.kind ?? ''}::${action.label ?? ''}`;
    }

    private stableActionId(action: ProactiveItemAction): string | null {
        const body = action.body as Record<string, unknown> | undefined;
        if (body && typeof body['message_id'] === 'string' && body['message_id']) {
            return `mid:${body['message_id']}`;
        }
        if (body && typeof body['approval_id'] === 'string' && body['approval_id']) {
            return `apv:${body['approval_id']}`;
        }
        // RSVP and other actions carry their identity only in the url (the
        // event id is a path segment); a row index key would move onto the
        // next row once this one is spliced out.
        const url = (action.url ?? '').trim();
        if (url) {
            return `url:${action.kind ?? ''}:${url}`;
        }
        return null;
    }

    isFiring(itemId: string, sectionLabel: string, rowIndex: number, action: ProactiveItemAction): boolean {
        return this.firingKeys().has(this.actionKey(itemId, sectionLabel, rowIndex, action));
    }

    /** Locked by its own key, or by the message it is about: once a send or
     *  a hand-off happened for that message on any surface (lockHappened),
     *  every action on it is spent, wherever it is rendered or dispatched. */
    isResolved(itemId: string, sectionLabel: string, rowIndex: number, action: ProactiveItemAction): boolean {
        return (
            this.resolvedKeys().has(this.actionKey(itemId, sectionLabel, rowIndex, action)) ||
            this.isMessageResolved(actionMessageId(action))
        );
    }

    private timer: number | null = null;
    private firstLoadDone = false;
    private sseSub: Subscription | null = null;

    /** The signed-in user the scope was read for. Plain field: the effect's
     *  own bookkeeping, never rendered. */
    private scopeUserId: number | null | undefined = undefined;

    /** Root singleton: the scope outlives a sign-out and a sign-in as another
     *  account with no page reload, so every identity change re-reads it for
     *  the new account. */
    // noinspection JSUnusedLocalSymbols
    private readonly _identityEffect = effect(() => {
        const userId = this.auth.state().user?.user_id ?? null;
        if (this.scopeUserId === userId) {
            return;
        }
        this.scopeUserId = userId;
        untracked(() => this.teamWide.set(readTeamWide(userId)));
    });

    // noinspection JSUnusedLocalSymbols
    private readonly _onAuth = effect(() => {
        // Gated on the DDR release flag, not just auth: when LaunchDarkly says
        // this user isn't released, every /api/ddr/* poll would 403 forever.
        const isAuthed = !!this.auth.state().user && this.auth.ddrEnabled();
        if (isAuthed) {
            void this.refresh();
            this.startPolling();
            this.startSse();
        } else {
            this.stopPolling();
            this.stopSse();
            this.items.set([]);
        }
    });

    constructor() {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.stopPolling();
            } else if (this.auth.state().user && this.auth.ddrEnabled()) {
                void this.refresh();
                this.startPolling();
            }
        });
    }

    private startSse(): void {
        if (this.sseSub) {
            return;
        }
        // Subscribe to the existing user-events SSE pipe; refresh the rail
        // immediately on any `proactive_activity` push so the user sees
        // job runs / trigger fires / EOD pushes without the 60s poll wait.
        this.sseSub = this.threadEvents.connect().subscribe(evt => {
            if ((evt as { type?: string }).type === 'proactive_activity') {
                void this.refresh();
            }
        });
    }

    private stopSse(): void {
        if (this.sseSub) {
            this.sseSub.unsubscribe();
            this.sseSub = null;
        }
    }

    // noinspection JSUnusedLocalSymbols
    private readonly _refetchOnScopeChange = effect(() => {
        // Re-fetch whenever the scoped room set changes so the rail
        // matches whatever surface the user is on right now.
        this.scopeRoomIds();
        this.teamWide();
        if (this.auth.state().user && this.auth.ddrEnabled()) {
            void this.refresh();
        }
    });

    private refreshGeneration = 0;

    async refresh(): Promise<void> {
        // Last refresh wins. A scope flip (room to team, or back) refetches
        // while the previous scope's GET is still out; that older response
        // must not land last and show the wrong scope's feed.
        const generation = ++this.refreshGeneration;
        this.loading.set(true);
        try {
            const params = new URLSearchParams();
            params.set('limit', '40');
            // Up next is always the whole team's schedule, whatever the feed scope.
            params.set('team_upcoming', '1');
            if (!this.teamWide()) {
                for (const rid of this.scopeRoomIds()) {
                    if (rid) {
                        params.append('room_id', rid);
                    }
                }
            }
            const resp = await firstValueFrom(
                this.http.get<{ items: ProactiveActivityItem[]; upcoming?: UpcomingJob[] }>(
                    `${ROOT}/proactive/activity?${params.toString()}`
                )
            );
            if (generation !== this.refreshGeneration) {
                return;
            }
            this.items.set(resp?.items ?? []);
            this.upcoming.set(resp?.upcoming ?? []);
            this.error.set(null);
            this.firstLoadDone = true;
        } catch (err) {
            if (generation !== this.refreshGeneration) {
                return;
            }
            // Don't blow up the feed on a transient - keep last good list.
            if (!this.firstLoadDone) {
                this.items.set([]);
                this.upcoming.set([]);
            }
            this.error.set(err instanceof Error ? err.message : 'failed to load activity');
        } finally {
            if (generation === this.refreshGeneration) {
                this.loading.set(false);
            }
        }
    }

    /** Fire an inline section action (trust check-in Yes/Not yet/Never,
     *  Send draft, Open in Outlook, etc).
     *  On success, splice the matching row out of the section locally so the
     *  card collapses immediately; refresh the feed in the background to
     *  pick up any server-side cascade (e.g. card disappearing entirely once
     *  its last entry is gone). */
    async dispatchAction(itemId: string, sectionLabel: string, rowIndex: number, action: ProactiveItemAction): Promise<void> {
        const key = this.actionKey(itemId, sectionLabel, rowIndex, action);
        // Lock the button: don't re-fire while in-flight, don't fire again
        // after a successful resolve.
        if (this.firingKeys().has(key) || this.isResolved(itemId, sectionLabel, rowIndex, action)) {
            return;
        }
        const rawUrl = (action.url ?? '').trim();
        if (!rawUrl) {
            void this.toast.error(`${action.label} has no url`);
            return;
        }
        // open_web_link actions (Open in Outlook, etc): hand off to the
        // browser, do NOT POST against the third-party URL.
        if (action.kind === 'open_web_link') {
            window.open(rawUrl, '_blank', 'noopener,noreferrer');
            this.markResolved(key);
            return;
        }
        // Build the absolute URL. The local proxy (proxy.conf.json) strips
        // a leading `/api` before forwarding to the backend, so we need to
        // pass the duplicate `/api/api/ddr/...` shape that ddr.service.ts
        // uses (its ROOT = `${apiBase}/api/ddr`). For prod / dev / staging
        // apiBase is the full https origin and the path stays as-is.
        // Stripping `/api` would short-circuit the proxy rewrite and the
        // backend would 404 on `/ddr/...`.
        const apiUrl = rawUrl.startsWith('http') ? rawUrl : `${environment.apiBase}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
        const method = (action.method ?? 'POST').toUpperCase();
        this.firingKeys.update(set => {
            const next = new Set(set);
            next.add(key);
            return next;
        });
        let result: ActionResult | null = null;
        try {
            result = (await firstValueFrom(
                this.http.request<ActionResult | null>(method, apiUrl, { body: action.body ?? {} })
            )) as ActionResult | null;
        } catch (err) {
            const msg = extractActionErrorMessage(err);
            this.error.set(msg);
            void this.toast.error(`${action.label} failed: ${msg}`);
            return;
        } finally {
            this.firingKeys.update(set => {
                const next = new Set(set);
                next.delete(key);
                return next;
            });
        }
        if (this.settleShortOfDone(key, action, result)) {
            return;
        }
        void this.toast.success(`${action.label} ✓`);
        this.markResolved(key);
        // Session-wide message_id resolved set - drives action-button
        // suppression on any surface (rail + chat system-event card).
        const bodyMid = action.body && typeof action.body['message_id'] === 'string' ? (action.body['message_id'] as string) : null;
        if (bodyMid) {
            this.markMessageResolved(bodyMid);
        }
        this.spliceRow(itemId, sectionLabel, rowIndex);
    }

    /** Everything the backend can answer short of a plain success. True when
     *  the click is settled here and the row stays: refused (nothing happened,
     *  button stays live), queued (an approval exists, the button locks), or
     *  the primary thing happened but its durable resolution did not save
     *  (Handle it and Send draft lock, Dismiss stays clickable for a retry). */
    settleShortOfDone(key: string, action: ProactiveItemAction, result: ActionResult | null): boolean {
        const outcome = actionOutcome(result);
        if (outcome === 'error') {
            void this.toast.error(`${action.label} failed: ${actionErrorText(result)}`);
            return true;
        }
        if (outcome === 'refused') {
            void this.toast.error(result?.reason || `${action.label} was refused.`);
            return true;
        }
        if (outcome === 'queued') {
            this.lockHappened(key, action);
            void this.toast.info(result?.reason || `${action.label} is waiting for your approval.`);
            return true;
        }
        if (outcome === 'unresolved') {
            if (actionCannotRepeat(action.kind)) {
                this.lockHappened(key, action);
            }
            void this.toast.warning(result?.warning || `${action.label} was noted, but the item may come back after a refresh.`);
            return true;
        }
        return false;
    }

    /** Something irreversible happened for this message (a send, a queued
     *  approval, a prompt handed to the DDR). Lock the button here AND mark
     *  the message resolved session-wide, so the same Send is not offered
     *  again from the chat card or the review dialog (review, 2026-09-22). */
    lockHappened(key: string, action: { body?: Record<string, unknown> }): void {
        this.markResolved(key);
        const mid = actionMessageId(action);
        if (mid) {
            this.markMessageResolved(mid);
        }
    }

    /** For actions the rail carries out itself (ask_ddr): lock the button the
     *  same way a POSTed action does once it has fired. */
    markActionResolved(itemId: string, sectionLabel: string, rowIndex: number, action: ProactiveItemAction): void {
        this.markResolved(this.actionKey(itemId, sectionLabel, rowIndex, action));
    }

    /** Hold or release the in-flight lock for work the rail does outside
     *  dispatchAction (the DM send behind Handle it), so the button reads
     *  "Sending" and cannot double-fire while that work is pending. */
    setActionFiring(itemId: string, sectionLabel: string, rowIndex: number, action: ProactiveItemAction, firing: boolean): void {
        const key = this.actionKey(itemId, sectionLabel, rowIndex, action);
        this.firingKeys.update(set => {
            if (set.has(key) === firing) {
                return set;
            }
            const next = new Set(set);
            if (firing) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });
    }

    private markResolved(key: string): void {
        this.resolvedKeys.update(set => {
            const next = new Set(set);
            next.add(key);
            return next;
        });
    }

    /** Optimistic row removal so the rail collapses an actioned row right
     *  away. We deliberately do NOT refetch here: the backend system_event
     *  card is a frozen snapshot and would return the same rows back,
     *  undoing the update. The natural polling interval picks up the
     *  eventual state. */
    spliceRow(itemId: string, sectionLabel: string, rowIndex: number): void {
        this.items.update(list =>
            list.map(it => {
                if (it.id !== itemId) {
                    return it;
                }
                const sections = (it.sections ?? []).map(s => {
                    if (s.label !== sectionLabel) {
                        return s;
                    }
                    const rows = s.items.filter((_, i) => i !== rowIndex);
                    return { ...s, items: rows, count: rows.length };
                });
                return { ...it, sections };
            })
        );
    }

    private startPolling(): void {
        this.stopPolling();
        this.timer = window.setInterval(() => {
            void this.refresh();
        }, POLL_INTERVAL_MS);
    }

    private stopPolling(): void {
        if (this.timer !== null) {
            window.clearInterval(this.timer);
            this.timer = null;
        }
    }
}

/** Pull a useful error string out of whatever the HTTP client threw.
 *  FastAPI returns `{detail: "..."}`; Angular's HttpErrorResponse parses
 *  it into `.error.detail`. Fall back to `.error.message`, then
 *  `.statusText`, then `.message`. Generic 'action failed' is the last
 *  resort - and we still want to surface the status code so a 404 vs
 *  500 vs 400 is debuggable from the toast. */
function extractActionErrorMessage(err: unknown): string {
    if (!err || typeof err !== 'object') {
        return 'action failed';
    }
    const e = err as Record<string, unknown>;
    const status = typeof e['status'] === 'number' ? (e['status'] as number) : 0;
    const errorBody = e['error'] as Record<string, unknown> | string | null | undefined;
    let detail = '';
    if (typeof errorBody === 'string') {
        detail = errorBody;
    } else if (errorBody && typeof errorBody === 'object') {
        const d = (errorBody as Record<string, unknown>)['detail'];
        const m = (errorBody as Record<string, unknown>)['message'];
        detail = typeof d === 'string' ? d : typeof m === 'string' ? m : '';
    }
    const statusText = typeof e['statusText'] === 'string' ? (e['statusText'] as string) : '';
    const msg = typeof e['message'] === 'string' ? (e['message'] as string) : '';
    const parts = [detail, statusText, msg].filter(Boolean);
    if (parts.length === 0) {
        return status ? `HTTP ${status}` : 'action failed';
    }
    return status ? `HTTP ${status} · ${parts[0]}` : parts[0];
}
